import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListItem } from '../../types/list';
import { BaseDetailCard } from '../shared/BaseDetailCard';
import { AppStoreIcon, GooglePlayIcon } from '../icons';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useSection } from '../../hooks/useSection';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { useDetailCardTranslation } from '../../hooks/useDetailCardTranslation';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { getTranslatedText, getTranslatedCategory } from '../../utils/translationHelpers';

interface DetailCardProps {
  item: ListItem;
  items: ListItem[];
  onClose: () => void;
  onItemChange: (item: ListItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  tableId: string;
  categoryColors: Map<string, string>;
}

export const DetailCard: React.FC<DetailCardProps> = ({
  item,
  items,
  onClose,
  onItemChange,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  tableId,
  categoryColors
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const { displayImage, shouldDisplay } = useImageDisplay(item.image, 'detailCard');

  const analytics = useAnalytics({
    sectionId: section.id,
    sectionName: section.names.fr,
    sectionType: 'liste'
  });

  const { getDescription, getOffer, translatingDescription, descriptionError } = useDetailCardTranslation({
    itemId: item.id,
    itemName: item.name,
    description: item.description,
    offer: item.offer,
    descriptions: item.descriptions,
    offers: item.offers,
    tableId,
    sectionType: 'liste'
  });

  const { isFavorite, handleFavoriteToggle } = useFavoriteToggle({
    itemId: item.id,
    itemName: item.name,
    category: item.category,
    type: item.type,
    sectionType: 'liste',
    itemData: item
  });

  useEffect(() => {
    const startTime = Date.now();
    return () => {
      const duration = (Date.now() - startTime) / 1000;
      analytics.trackEvent('content_view', 'detail_card_view', {
        item_id: item.id,
        item_name: item.name,
        item_type: 'liste',
        category: item.category,
        type: item.type,
        duration,
        source: 'detail_card'
      });
    };
  }, [item.id, analytics]);

  const renderContent = () => null;

  const renderBookingButton = () => {
    if (!item.appstore && !item.playstore) return null;

    return (
      <div className="flex flex-col gap-2 mb-4">
        {item.appstore && (
          <a
            href={item.appstore}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              analytics.trackItemAction(item.id, 'app_store_click', 'liste', {
                item_name: item.name,
                category: item.category,
                type: item.type,
                url: item.appstore
              });
            }}
            className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
            style={{ backgroundColor: config['major-color'] }}
          >
            <AppStoreIcon size={18} className="mr-2" />
            {getTranslatedText(translations, 'btn_app_store', i18n.language)}
          </a>
        )}
        {item.playstore && (
          <a
            href={item.playstore}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              analytics.trackItemAction(item.id, 'play_store_click', 'liste', {
                item_name: item.name,
                category: item.category,
                type: item.type,
                url: item.playstore
              });
            }}
            className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
            style={{ backgroundColor: config['major-color'] }}
          >
            <GooglePlayIcon size={18} className="mr-2" />
            {getTranslatedText(translations, 'btn_play_store', i18n.language)}
          </a>
        )}
      </div>
    );
  };

  const translate = (key: string) => getTranslatedText(translations, key, i18n.language);
  const translateCategory = (key: string) => getTranslatedCategory(translations, key, i18n.language);

  return (
    <BaseDetailCard
      item={item}
      items={items}
      onClose={onClose}
      onItemChange={onItemChange}
      config={config}
      translations={translations}
      tableId={tableId}
      renderContent={renderContent}
      renderBookingButton={renderBookingButton}
      onCategorySelect={onCategorySelect}
      onTypeSelect={onTypeSelect}
      categoryColors={categoryColors}
      getTranslatedCategory={translateCategory}
      getTranslatedText={translate}
      context={{
        sectionType: 'liste',
        itemId: item.id,
        section_id: section.id,
        contentType: 'description'
      }}
      isFavorite={isFavorite}
      onFavoriteToggle={handleFavoriteToggle}
      image={shouldDisplay ? displayImage : undefined}
      description={getDescription()}
      offer={getOffer()}
      translating={translatingDescription}
      translationError={descriptionError}
    />
  );
};

export default DetailCard;
